// gtSearch — shared pieces for the search engine (C1-SEARCH, 25 Oct 2026).
//
// SearchMatchLine  "CV · …led the Kubernetes migration…" under a row's name
// SearchHint       the line under a search box: how it was read, meaning or
//                  words, the index catching up, and the search tips
// splitSearch      a saved view's tree split into the box text and the rest
// useDeepMatch     which people in a list already on screen (a bench, a
//                  pipeline) match a search across their whole record
import { useEffect, useRef, useState } from 'react';

export const SEARCH_PLACEHOLDER = 'Search everything: skills, CV, notes, answers…';

// ── Why it matched ──────────────────────────────────────────────────────────
export function SearchMatchLine({ m, className }) {
  if (!m || !Array.isArray(m.segments) || !m.segments.length) return null;
  return (
    <div className={'gtsx-match' + (m.ai ? ' gtsx-match--ai gtsx-match--' + (/close/i.test(m.label) ? 'close' : 'fit') : '') + (className ? ' ' + className : '')} title={m.label + ': ' + m.segments.map(s => s.t).join('')}>
      <span className="gtsx-match-l">{m.ai ? <Spark /> : null}{String(m.label || '').replace(/^AI: /, '').split(' · ')[0]}</span>
      <span className="gtsx-match-t">
        {m.segments.map((s, i) => (s.h ? <mark key={i}>{s.t}</mark> : <span key={i}>{s.t}</span>))}
      </span>
    </div>
  );
}

// ── Tips ────────────────────────────────────────────────────────────────────
export const SEARCH_TIPS = [
  ['kubernetes london', 'every word, anywhere in the record'],
  ['"head of data"', 'this exact phrase'],
  ['java OR kotlin', 'either one'],
  ['-recruiter', 'leave these out (or NOT recruiter)'],
  ['(java OR kotlin) fintech', 'brackets group words'],
  ['title:cto  company:monzo', 'search one field'],
  ['past:mckinsey  current:revolut', 'past or current employer only'],
  ['skill:rust  location:berlin  school:oxford', 'more fields'],
  ['ex-MBB senior consultants based in the UK', 'AI reads a sentence and searches for it'],
];

export function SearchTips({ compact }) {
  return (
    <div className={'gtsx-tips' + (compact ? ' gtsx-tips--compact' : '')}>
      <div className="gtsx-tips-head">Search reads CVs, skills, experience, notes, form answers, interview answers and email replies. Typos are forgiven.</div>
      <div className="gtsx-tips-grid">
        {SEARCH_TIPS.map(([ex, what]) => (
          <div key={ex} className="gtsx-tip"><code>{ex}</code><span>{what}</span></div>
        ))}
      </div>
    </div>
  );
}

// ── The line under a search box ─────────────────────────────────────────────
// info comes back from the list endpoint: { mode, meaning, meaning_available,
// interpreted, indexing }. mode is what the user picked: auto or keywords.
export function Spark({ className }) {
  return (
    <svg className={className || 'gtsx-spark'} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9L12 2.5z" />
      <path d="M19 15l.8 2.2 2.2.8-2.2.8L19 21l-.8-2.2-2.2-.8 2.2-.8L19 15z" />
    </svg>
  );
}

// C1-AISEARCH: what the AI understood, with a way to take over the search.
export function AiReading({ ai, onQuery, onMode }) {
  if (!ai) return null;
  return (
    <div className="gtsx-ai">
      <span className="gtsx-ai-chip"><Spark />AI search</span>
      <span className="gtsx-ai-sum">
        {ai.summary || 'Searching for what you described'}
        {ai.checked > 0 && <span className="gtsx-ai-count"> · checked {ai.checked} profile{ai.checked === 1 ? '' : 's'}: {ai.fits} fit{ai.close ? ', ' + ai.close + ' close' : ''}</span>}
      </span>
      <span className="gtsx-ai-acts">
        {onQuery && <button type="button" className="gtsx-hint-btn" title={ai.strict} onClick={() => onQuery(ai.strict)}>Edit as Boolean</button>}
        {onMode && <button type="button" className="gtsx-hint-btn" onClick={() => onMode('keywords')}>Words only</button>}
      </span>
      {ai.note && <span className="gtsx-ai-note">{ai.note}</span>}
    </div>
  );
}

export function SearchHint({ info, query, mode, onMode, onQuery }) {
  const [tips, setTips] = useState(false);
  const q = String(query || '').trim();
  if (!q) return null;
  const bits = [];
  if (info && info.interpreted) bits.push(<span key="i" className="gtsx-hint-read">Read as <b>{info.interpreted}</b></span>);
  if (info && info.mode === 'ai' && info.ai) {
    bits.push(<AiReading key="ai" ai={info.ai} onQuery={onQuery} onMode={onMode} />);
  } else if (info && info.mode === 'meaning') {
    bits.push(
      <span key="m" className="gtsx-hint-mode">
        <span className="gtsx-dot" />
        {info.meaning ? 'Searching by meaning, plus every word match' : info.meaning_available ? 'Matching on words while the meaning index catches up' : 'Matching on words'}
        {onMode && <button type="button" className="gtsx-hint-btn" onClick={() => onMode('keywords')}>Words only</button>}
      </span>
    );
  } else if (mode === 'keywords' && onMode && q.split(/\s+/).length >= 4) {
    bits.push(<span key="k" className="gtsx-hint-mode">Words only <button type="button" className="gtsx-hint-btn" onClick={() => onMode('auto')}>Use AI search</button></span>);
  }
  if (info && info.indexing > 0) bits.push(<span key="x" className="gtsx-hint-index">Updating the search index: {info.indexing} record{info.indexing === 1 ? '' : 's'}</span>);
  return (
    <div className="gtsx-hint">
      {bits}
      <button type="button" className="gtsx-hint-help" onClick={() => setTips(v => !v)} aria-expanded={tips}>{tips ? 'Hide search tips' : 'Search tips'}</button>
      {tips && <div className="gtsx-hint-pop"><SearchTips compact /></div>}
    </div>
  );
}

// ── Saved views: the search lives in the tree ───────────────────────────────
// A saved view's tree may hold { field: 'search', ... }. The filter rail does
// not know that field, so it goes back into the search box instead.
export function splitSearch(tree) {
  if (!tree || !Array.isArray(tree.and)) return { tree, q: '', mode: 'auto' };
  let q = '';
  let mode = 'auto';
  const rest = tree.and.filter((c) => {
    if (c && c.field === 'search') { q = String(c.value || ''); mode = c.mode === 'keywords' ? 'keywords' : 'auto'; return false; }
    return true;
  });
  return { tree: rest.length ? { ...tree, and: rest } : null, q, mode };
}

export function searchCondition(q, mode) {
  const v = String(q || '').trim();
  if (!v) return null;
  const c = { field: 'search', op: 'matches', value: v };
  if (mode === 'keywords') c.mode = 'keywords';
  return c;
}

// Sort: best match first while searching, unless a column was clicked.
export function searchSort(sort, q) {
  if (String(q || '').trim() && sort && sort.field === 'created_at' && sort.dir === 'desc') return { field: 'relevance', dir: 'desc' };
  return sort;
}

// ── Search inside a list already on screen ──────────────────────────────────
// Returns { ids: Set of candidate ids that match, matches: { id: match } }.
// The screen keeps its own instant name filter and adds these.
const EMPTY = { ids: new Set(), matches: {}, q: '' };
export function useDeepMatch(authFetch, q, ids) {
  const [res, setRes] = useState(EMPTY);
  const seq = useRef(0);
  // A parent may hand down a new authFetch on every render; reading it from a
  // ref keeps this from searching again (and again) for the same words.
  const fetchRef = useRef(authFetch);
  fetchRef.current = authFetch;
  const key = (ids || []).join(',');
  useEffect(() => {
    const text = String(q || '').trim();
    const list = key ? key.split(',').map(Number).filter(Number.isFinite) : [];
    if (text.length < 2 || !list.length || /^(GTC-[A-Z0-9]+[\s,]*)+$/i.test(text)) { setRes(EMPTY); return undefined; }
    const my = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await fetchRef.current('/api/search/match', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ q: text, ids: list }),
        });
        if (my === seq.current) setRes({ ids: new Set(r.ids || []), matches: r.matches || {}, q: text });
      } catch (e) { if (my === seq.current) setRes(EMPTY); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, key]);
  return res;
}

// Pool search for the "add people" pickers: ranked, whole record.
export async function searchPool(authFetch, q, limit) {
  const r = await authFetch('/api/search/candidates', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ q: String(q || '').trim(), limit: limit || 40 }),
  });
  return r.rows || [];
}

// ── Roles on the candidate portal ───────────────────────────────────────────
// Every word must appear somewhere in the role: title, place, pattern, field,
// the whole advert or the company line. Accents and case ignored. Title
// matches first, otherwise the board's own order.
const foldText = (s) => String(s == null ? '' : s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const plainHtml = (h) => String(h || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
export function filterRoles(roles, q) {
  const words = foldText(q).split(/[^a-z0-9+#.]+/).filter(w => w.length > 1);
  if (!words.length) return roles || [];
  const scored = [];
  (roles || []).forEach((r, i) => {
    const title = foldText(r.title);
    const hay = [title, foldText(r.public_location), foldText(r.public_work_pattern), foldText(r.expertise_domain),
      foldText(plainHtml(r.advert_html)), foldText(r.client_blurb)].join(' ');
    if (!words.every(w => hay.indexOf(w) !== -1)) return;
    scored.push({ r, i, t: words.every(w => title.indexOf(w) !== -1) ? 0 : 1 });
  });
  return scored.sort((a, b) => a.t - b.t || a.i - b.i).map(x => x.r);
}
